import { Component } from '@theme/component';

/**
 * `<echt-redo-cart-hook>` — gives ReDo the DOM it is configured to look for on
 * the CART PAGE, without editing a stock file.
 *
 * ReDo (returns + package protection) is enabled as an app embed on this theme.
 * Its merchant config, fetched at runtime from
 * `shopify-cdn.getredo.com/widgets/autocheck/?storeUrl=echt-sportswear.myshopify.com`,
 * places its two cart widgets with an explicit selector list rather than a
 * heuristic — for the cart page:
 *
 *     ".cart__checkout-buttons > [data-dummycheckout]", insertionPosition "after"
 *
 * ReDo's loader (main.js -> chunk 545, modules 55556 `T` and 34704 `tG`) runs a
 * plain `document.querySelectorAll()` on that string and inserts a
 * `<redo-shopify-toggle>` next to each match. Horizon's cart page has neither
 * class: the checkout button is stock `snippets/cart-summary.liquid`'s
 * `.cart__ctas > button#checkout[name=checkout]`. So the query found nothing
 * and ReDo rendered nothing — Jeremy, 6 Sep 2026: "Returns protection hasn't
 * been migrated over (redo)".
 *
 * Live builds this shape in Liquid (`~/dev/echt/sections/main-cart.liquid`
 * 1259-1309): a `.cart__checkout-buttons` wrapper holding the real `#checkout`
 * button and a `[data-dummycheckout]` stand-in next to it. Stock files may not
 * be edited here, so the same shape is grafted on from JS. That keeps ReDo's
 * dashboard configuration byte-for-byte identical to live (HANDBOOK §6,
 * same-backend rule).
 *
 * Why this must exist alongside the drawer hook, not instead of it: once ReDo
 * finds a valid placement it injects a document-level stylesheet hiding
 * `[name="checkout"],.cust-bottom-buttons,.cart-drawer-form__checkout-buttons,[data-dummycheckout]`
 * (chunk 178, `lo` / `foundValidButtonSelectors`) and renders its own pair of
 * checkout buttons in the slot. Hooking only the drawer would hide the cart
 * page's checkout button with nothing put back.
 *
 * The stand-in is an inert `<span hidden>`, not live's clickable button: here
 * the real checkout button is still visible and functional until ReDo replaces
 * it, so the stand-in only ever has to be an anchor for `insertAdjacent`. If
 * ReDo never loads, nothing about the page changes.
 */
class EchtRedoCartHook extends Component {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();

    this.#apply();

    // The cart page re-renders through the Section Rendering API on every
    // quantity change and discount edit, which replaces the summary subtree and
    // takes the graft with it. Observing is simpler and more reliable than
    // guessing which events morph what — and #apply() is idempotent, so the
    // mutations it makes settle on the first pass instead of looping.
    this.#observer = new MutationObserver(() => {
      if (this.#frame != null) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = undefined;
        this.#apply();
      });
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
  }

  /**
   * Adds ReDo's cart-page hook to stock's checkout CTAs. Safe to call any
   * number of times: it does nothing once the shape is already there.
   */
  #apply() {
    // Scope to the cart form's own submit button rather than any [name=checkout]
    // on the page, so this can never graft onto the drawer (which is not
    // rendered on /cart anyway) or a third-party widget.
    const checkout = document.querySelector('.cart__ctas button[name="checkout"], .cart__ctas #checkout');
    const container = checkout?.parentElement;
    if (!container) return;

    container.classList.add('cart__checkout-buttons');

    if (container.querySelector(':scope > [data-dummycheckout]')) return;

    const standin = document.createElement('span');
    standin.setAttribute('data-dummycheckout', '');
    standin.hidden = true;
    checkout.after(standin);

    // ReDo re-places on its own document-wide MutationObserver, but only when
    // its attachment strategy is a checkout-button one — which this store's is
    // ("checkout-button-product"). The nudge below costs nothing and makes the
    // first paint after a graft immediate rather than debounced. Both event
    // names come from ReDo's own enum (main.js: `redo:reload-cart` refetches
    // the cart, `redo:re-render-elements` re-renders placed widgets) and are
    // listened for on `document`.
    document.dispatchEvent(new CustomEvent('redo:reload-cart'));
    document.dispatchEvent(new CustomEvent('redo:re-render-elements'));
  }
}

if (!customElements.get('echt-redo-cart-hook')) {
  customElements.define('echt-redo-cart-hook', EchtRedoCartHook);
}
